import React, { useContext } from "react";
import { useSiteConfig } from "../context/SiteConfigContext";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { Mail, Phone } from "lucide-react"; // 🌟 Synced clean vector lines
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import { CategoryContext } from "../context/CategoryContext";

function Footer() {
  const { config } = useSiteConfig();
  const { categories } = useContext(CategoryContext);

  return (
    <>
      <footer className="orangeapes-editorial-footer-panel">
        <Container>
          <Row className="g-md-5 g-3">
            
            {/* COLUMN 1: THE BRAND PROFILE ACCENT */}
            <Col lg={3} md={6}>
              <div className="footer-brand-identity-stack">
                <div className="footer-brand-logo-holder mb-3">
                  <img 
                    src={config?.logo || "https://theorangeapes.com/frontend/img/logo.webp"} 
                    alt="Brand Logo" 
                    className="editorial-footer-logo"
                  />
                </div>
                {/* <h5 className="footer-brand-name-string mb-3">ORANGEAPES</h5> */}
                <p className="footer-brand-bio-copy mb-4">
                  Curating premium statement drops and signature boutique formulas engineered with precise structural minimalism.
                </p>
                
                {/* Clean Contact Vectors Row */}
                <div className="footer-contact-links-box mb-4">
                  {config?.email && (
                    <a href={`mailto:${config.email}`} className="footer-contact-line-anchor">
                      <Mail size={14} className="text-orange-token" />
                      <span>{config.email}</span>
                    </a>
                  )}
                  {config?.phone && (
                    <a href={`tel:${config.phone}`} className="footer-contact-line-anchor mt-2">
                      <Phone size={14} className="text-orange-token" />
                      <span>+91 {config.phone}</span>
                    </a>
                  )}
                </div>

                {/* Social Networks Icons Matrix */}
                <div className="d-flex gap-3 align-items-center footer-socials-matrix">
                  {config?.socialMedia?.facebook && (
                    <a href={config.socialMedia.facebook} target="_blank" rel="noopener noreferrer">
                      <FaFacebook size={16} />
                    </a>
                  )}
                  {config?.socialMedia?.instagram && (
                    <a href={config.socialMedia.instagram} target="_blank" rel="noopener noreferrer">
                      <FaInstagram size={16} />
                    </a>
                  )}
                  {config?.socialMedia?.twitter && (
                    <a href={config.socialMedia.twitter} target="_blank" rel="noopener noreferrer">
                      <FaTwitter size={16} />
                    </a>
                  )}
                  {config?.socialMedia?.youtube && (
                    <a href={config.socialMedia.youtube} target="_blank" rel="noopener noreferrer">
                      <FaYoutube size={16} />
                    </a>
                  )}
                </div>
              </div>
            </Col>

            {/* 🌟 NEW COLUMN 2: DYNAMIC STUDIO SECTIONS */}
            <Col lg={3} md={6}>
              <h6 className="footer-column-caps-title mb-4">STUDIO CAPSULES</h6>
              <ul className="list-unstyled footer-navigation-links-list">
                {categories && categories.slice(0, 5).map((cat) => (
                  <li key={cat._id}>
                    <Link to={`/categories/${cat.slugPath}`}>{cat.name.toUpperCase()}</Link>
                  </li>
                ))}
                <li><Link to="/shop">EXPLORE ALL</Link></li>
              </ul>
            </Col>

            {/* COLUMN 3: CORPORATE REGISTRIES INFO */}
            <Col lg={3} md={6}>
              <h6 className="footer-column-caps-title mb-4">REGISTRIES & INFO</h6>
              <ul className="list-unstyled footer-navigation-links-list">
                {/* <li><Link to="/about-us">OUR STORY</Link></li> */}
                <li><Link to="/contact-us">CONTACT US</Link></li>
                <li><Link to="/return-and-refund-policy">RETURN POLICY</Link></li>
                {/* <li><Link to="/shipping-policy">SHIPPING LOGISTICS</Link></li> */}
                <li><Link to="/privacy-policy">PRIVACY POLICY</Link></li>
                <li><Link to="/terms-and-conditions">TERMS & CONDITIONS</Link></li>
              </ul>
            </Col>

            {/* COLUMN 4: GATEWAYS VERIFICATION */}
            <Col lg={3} md={6}>
              <h6 className="footer-column-caps-title mb-4">SECURE GATEWAYS</h6>
              <p className="footer-brand-bio-copy mb-4">
                Encrypted pipeline integrations processing dynamic tokenized checkout vaults.
              </p>
              <div className="footer-payment-badge-matrix-box">
                <div className="payment-gateway-row mb-3">
                  <span className="gateway-type-header">UPI NETWORKS</span>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <img src="https://cdn.razorpay.com/app/googlepay.svg" alt="GPay" width="22" height="22" />
                    <img src="https://cdn.razorpay.com/app/phonepe.svg" alt="PhonePe" width="22" height="22" />
                    <img src="https://cdn.razorpay.com/app/paytm.svg" alt="Paytm" width="22" height="22" />
                  </div>
                </div>
                <div className="payment-gateway-row">
                  <span className="gateway-type-header">CARDS & SCHEMES</span>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <img src="https://cdn.razorpay.com/card-networks/visa.svg" alt="Visa" width="22" height="22" />
                    <img src="https://cdn.razorpay.com/card-networks/mastercard.svg" alt="Mastercard" width="22" height="22" />
                    <img src="https://cdn.razorpay.com/card-networks/rupay.svg" alt="RuPay" width="22" height="22" />
                  </div>
                </div>
              </div>
            </Col>

          </Row>

          {/* BASE TERMINAL ANCHOR */}
          <div className="orangeapes-footer-bottom-bar-terminal mt-5 pt-4 d-flex flex-column flex-md-row align-items-center justify-content-between">
            <p className="footer-copyright-string-txt mb-3 mb-md-0">
              ALL RIGHTS RESERVED &copy; {new Date().getFullYear()} ORANGEAPES STUDIO
            </p>
            <div className="footer-designer-attribution-badge">
              <a href="https://axesell.com" target="_blank" rel="noopener noreferrer" className="opacity-75">
                <img 
                  src="/images/axesell_logo.png" 
                  alt="Axesell Studio" 
                  className="axesell-bottom-logo" 
                  onError={(e) => { e.target.style.display = 'none'; }} 
                />
              </a>
            </div>
          </div>

        </Container>
      </footer>
    </>
  );
}

export default Footer;